import { BoardRowType, CustomColumnData, Task } from "@common-models/task";
import { Button, CircularProgress } from "@mui/material";
import { Stack } from "@mui/system";
import { boardsSelectOneObjectByQuery, isTempId, taskColumnSelectByQuery } from "@common-reducers/DBServiceThunks";
import { taskActions } from "@common-reducers/TasksReducer";
import { addNewTaskThunk } from "@common-reducers/TaskThunks";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { memo, useEffect, useMemo, useState } from "react";
import { MdAdd } from "react-icons/md";
import classes from "./new-task-row.module.scss";
import { useAnalyticsService } from "@common-reducers/hooks/use-analytics-service";
import { AnalyticsService } from "@common-services/analytics-service";
import { ProductEvent, trackProductEvent } from "@common-services/product-analytics";
import { waQueryContactsAndChatsTool } from "@common/tools/whatsapp";
import AddIcon from '@mui/icons-material/Add';
import AddContactsToCampaignDialog from "./add-contacts-to-campaign-dialog";
import { buildContactCustomData, findNameColumns, selectedContactDisplayName } from "./contact-task-helpers";
import { SelectedContact } from "@extension/context/content/components/shared";
import { useTranslation } from "react-i18next";
import { namespace } from "@common/config/constants";

export interface Props {
  disabled?: boolean
  outsideStyles?: any
  boardId?: string;
  groupId?: string;
  parentTaskId?: string;
  customData?: Record<string, CustomColumnData>;
  onEnter?: undefined | any
  onChangeText?: undefined | any
  textValue?: undefined | any
}

function NewTaskRowInput({ groupId,
  boardId, disabled, parentTaskId, outsideStyles,
  customData, onEnter, onChangeText, textValue }: Props) {
  const analyticsService: AnalyticsService = useAnalyticsService();
  const dispatch = useAppDispatch()
  const { t } = useTranslation(namespace);
  const IsNewSubTask = parentTaskId ? true : false;
  const activeNewTaskRow = useAppSelector((state) => state.TasksReducer.activeNewTaskRow)
  const [newTaskText, setnewTaskText] = useState<string>('')
  const boardType = useAppSelector((state) => boardsSelectOneObjectByQuery(state.DBBoardsReducer, { _id: boardId })?.type)

  const [isAddContactsDialogOpen, setIsAddContactsDialogOpen] = useState(false);

  const boardColumns = useAppSelector((state) =>
    taskColumnSelectByQuery(state.DBTaskColumnReducer, { boardId, deleted: false })
  );
  const whatsappIdColumn = useMemo(
    () => boardColumns.find((col) => col.type === 'whatsapp-id-cell'),
    [boardColumns]
  );
  // First/last-name columns are looked up by title prefix; populating them on
  // contact-add keeps the row consistent with what edit-cell does later.
  const nameColumns = useMemo(() => findNameColumns(boardColumns), [boardColumns]);

  useEffect(() => {
    if (boardType === 'audience') {
      try {
        waQueryContactsAndChatsTool({})
      } catch (e) {
        console.error("[waQueryContactsAndChatsTool] error", e)
      }
    }
  }, [dispatch, boardType]);

  const addContactsAsTasks = (contacts: SelectedContact[]) => {
    if (!whatsappIdColumn?._id) {
      console.error('WhatsappId column not found for board:', boardId);
      return;
    }
    const whatsappIdColumnId = whatsappIdColumn._id;
    contacts.forEach((contact) => {
      const displayName = selectedContactDisplayName(contact);
      const customData = buildContactCustomData({
        whatsappIdColumnId,
        contactId: contact.id,
        displayName,
        ...nameColumns,
      });
      const task: Task = {
        text: displayName || 'Change this contact name',
        boardId,
        groupId,
        taskType: BoardRowType.task,
        customData,
      };
      trackProductEvent(ProductEvent.CreateTask, {
        category: "userEngagement",
        action: "create new task with contact",
      });
      dispatch(addNewTaskThunk(task));
    });
  };

  function preparedAutoFocusData() {
    if (!groupId) return;
    dispatch(taskActions.setActiveNewTaskRow({
      groupId,
      parentTaskId,
    }))
  }


  async function addNewTask() {
    if (newTaskText === "" || newTaskText === "\n") {
      setnewTaskText('')
      return;
    }
    let task: Task = {
      text: newTaskText,
      boardId,
      groupId,
    };


    if (customData) {
      task = {
        ...task,
        customData
      }
    }


    if (IsNewSubTask) {
      task = {
        ...task,
        taskType: BoardRowType.subTask,
        parentTaskId: parentTaskId,

      }
    }
    if (!IsNewSubTask) {
      trackProductEvent(ProductEvent.CreateTask, {
        category: "userEngagement",
        action: "create new task",
      });

      task = {
        ...task,
        taskType: BoardRowType.task,
      }
    }

    dispatch(addNewTaskThunk(task))
    setnewTaskText('')
  }


  async function createMultipleTasksFromText(newTaskText: string) {
    const itemsList: string[] = newTaskText.split('\n').filter(e => e !== '')
    if (itemsList.length <= 1) {
      return;
    }

    itemsList.forEach((text) => {
      if (text && text !== '' && text !== '\n' && text !== '\r') {

        let task: Task = {
          text,
          boardId,
          groupId,
        }

        if (IsNewSubTask) {
          task = {
            ...task,
            taskType: BoardRowType.subTask,
            parentTaskId: parentTaskId,
          }
        }

        dispatch(addNewTaskThunk(task));
        setnewTaskText('')
      }
    });

    (newTaskText as any).b = 3
  }

  const isAutoFocused = () => {
    let isFocused = false
    if (activeNewTaskRow.groupId === groupId && activeNewTaskRow.parentTaskId === parentTaskId) {
      isFocused = true
    }
    return isFocused
  }

  const color = 'var(--tasks-text-muted)';

  const buildNewRowInput = () =>
    boardType == 'audience' ?
      <>
        <Stack sx={{ width: '100%', justifyContent: 'flex-start', pl: 1 }}>
          <Button
            data-testid="open-add-contacts-dialog"
            onClick={() => setIsAddContactsDialogOpen(true)}
            startIcon={<AddIcon />}
            size="small"
            sx={{
              alignSelf: 'flex-start',
              textTransform: 'none',
              color: 'var(--tasks-text-muted)',
              '&:hover': { color: 'var(--color-text)' },
            }}
          >
            {t('campaign.addContacts.openButton', 'Add contacts')}
          </Button>
          <AddContactsToCampaignDialog
            open={isAddContactsDialogOpen}
            onClose={() => setIsAddContactsDialogOpen(false)}
            onConfirm={addContactsAsTasks}
          />
        </Stack>
      </>
      :
      <>
        <span
          style={{ paddingInlineStart: IsNewSubTask ? 4 : 6 }}
          className={classes.plus_container}> <MdAdd color={color}
            style={{
              fontSize: '20px',
              marginInlineEnd: '4px'
            }} />
        </span>
        <textarea
          translate="no"
          disabled={disabled}
          data-testid='new-task-input-field'
          id={`footer-${groupId}`}
          onFocus={preparedAutoFocusData}
          rows={1}
          value={newTaskText}
          onChange={({ target }: any) => {
            if (!onChangeText) {
              setnewTaskText(target.value)
            } else {
              onChangeText(target.value)
            }
            createMultipleTasksFromText(target.value);
          }}
          onBlur={() => {
            if (!onEnter) {
              addNewTask()
            }
            else {
              onEnter()
            }
            dispatch(taskActions.setActiveNewTaskRow({
              groupId: '',
              parentTaskId: '',
            }))
          }}
          autoFocus={isAutoFocused()}
          onKeyDown={(event: any) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              if (!onEnter) {
                addNewTask()
              }
              else {
                onEnter()
              }
              return false;

            }

          }}
          className={`${classes.new_item_input}`}
          style={{
            height: "25px",
            resize: "none",
            overflow: "hidden",
            whiteSpace: "nowrap",
            backgroundColor: "transparent",
            width: IsNewSubTask ? "120px" : "88px",
            marginInlineStart: IsNewSubTask ? 5 : 0,
            unicodeBidi: 'plaintext',
            paddingInlineEnd: "10px",
            ...outsideStyles
          }}
          placeholder={IsNewSubTask ? "New Sub Item" : "New Item"} />
      </>;

  return (
    <Stack direction={"row"}>
      {isTempId(groupId ?? null) ? <CircularProgress sx={{ color, ml: 1 }} size={24} /> : buildNewRowInput()}
    </Stack>
  )
}

export default memo(NewTaskRowInput);
